import axios from 'axios';
import {
	GetSharedExternalEventsByIdParams,
	GetSharedExternalTraceByIdParams,
	GetSharedExternalTrailHistoryEventsParams,
	GetTrailHistoryParams,
} from '@/features/devices/device.types';
import { TrackingHistoryDto, TrackingHistoryEventDto } from '@/features/map/map.type';
import {
	CreateSharedExternalDto,
	SharedExternalDevicesDto,
	SharedExternalDto,
	ShareLinkDto,
} from '@/features/shareDevicesExternal/sharedExternal.dto';
import { ApiResult, ServiceResponse } from '@/OptimusRoutes/interfaces';
import { getJsDate } from '@/utilities/dateUtils';

interface ISharedExternalService {
	get: (deviceId: number) => Promise<ServiceResponse<ApiResult<SharedExternalDto[]>>>;
	create: (data: CreateSharedExternalDto) => Promise<ServiceResponse<SharedExternalDto>>;
	shareLink: (id: number, sharedLink: ShareLinkDto) => Promise<ServiceResponse>;
	getDevices: (token: string) => Promise<ServiceResponse<SharedExternalDevicesDto>>;
	delete: (id: number) => Promise<ServiceResponse>;
	getSharedExternalTraceById: (
		args: GetSharedExternalTraceByIdParams
	) => Promise<ServiceResponse<TrackingHistoryDto[]>>;
	getSharedExternalEventsById: (
		args: GetSharedExternalEventsByIdParams
	) => Promise<ServiceResponse<TrackingHistoryEventDto[]>>;
	getTrailHistory: (args: GetTrailHistoryParams) => Promise<ServiceResponse<TrackingHistoryDto[][]>>;
	getTrailHistoryEvents: (
		args: GetSharedExternalTrailHistoryEventsParams
	) => Promise<ServiceResponse<TrackingHistoryEventDto[]>>;
}

class Service implements ISharedExternalService {
	async get(): Promise<ServiceResponse<ApiResult<SharedExternalDto[]>>> {
		const { data } = await axios.get<ServiceResponse<ApiResult<SharedExternalDto[]>>>(
			'/v2/devices/shared-external/links'
		);
		return data;
	}

	async getDevices(token: string): Promise<ServiceResponse<SharedExternalDevicesDto>> {
		const params = {
			token,
			expire: getJsDate(),
		};
		const { data } = await axios.get<ServiceResponse<SharedExternalDevicesDto>>(
			'/v2/devices/shared-external/devices',
			{ params }
		);
		return data;
	}

	async getSharedExternalTraceById({
		deviceId,
		date,
		cancelToken,
	}: GetSharedExternalTraceByIdParams): Promise<ServiceResponse<TrackingHistoryDto[]>> {
		const params = {
			date,
		};
		const { data } = await axios.get<ServiceResponse<TrackingHistoryDto[]>>(
			`/v2/devices/${deviceId}/shared-external/track`,
			{ params, cancelToken }
		);
		return data;
	}

	async getSharedExternalEventsById({
		deviceId,
		date,
		cancelToken,
	}: GetSharedExternalEventsByIdParams): Promise<ServiceResponse<TrackingHistoryEventDto[]>> {
		const params = {
			date,
		};
		const { data } = await axios.get<ServiceResponse<TrackingHistoryEventDto[]>>(
			`/v2/devices/${deviceId}/shared-external/events`,
			{ params, cancelToken }
		);
		return data;
	}

	async create(sharedExternal: CreateSharedExternalDto): Promise<ServiceResponse<SharedExternalDto>> {
		const { data } = await axios.post<ServiceResponse<SharedExternalDto>>(
			'/v2/devices/shared-external',
			sharedExternal
		);
		return data;
	}

	async shareLink(id: number, sharedLink: ShareLinkDto): Promise<ServiceResponse> {
		const { data } = await axios.post<ServiceResponse>(`/v2/devices/shared-external/${id}/link`, sharedLink);
		return data;
	}

	getTrailHistory = async ({
		id,
		startDate,
		endDate,
		cancelToken,
	}: GetTrailHistoryParams): Promise<ServiceResponse<TrackingHistoryDto[][]>> => {
		const params = {
			'start-date': startDate,
			'end-date': endDate,
		};

		const { data } = await axios.get<ServiceResponse<TrackingHistoryDto[][]>>(
			`/v2/devices/${id}/shared-external/trail-history`,
			{
				params,
				cancelToken,
			}
		);
		return data;
	};

	getTrailHistoryEvents = async ({
		id,
		clientId,
		startDate,
		endDate,
		cancelToken,
	}: GetSharedExternalTrailHistoryEventsParams): Promise<ServiceResponse<TrackingHistoryEventDto[]>> => {
		const params = {
			clientId,
			'start-date': startDate,
			'end-date': endDate,
		};

		const { data } = await axios.get<ServiceResponse<TrackingHistoryEventDto[]>>(
			`/v2/devices/${id}/shared-external/trail-history/events`,
			{
				params,
				cancelToken,
			}
		);
		return data;
	};

	async delete(id: number): Promise<ServiceResponse> {
		const { data } = await axios.delete<ServiceResponse>(`/v2/devices/shared-external/${id}`);
		return data;
	}
}

const SharedExternalService = new Service();

export default SharedExternalService;
